import { createComponent } from '@front/utils/alpine';
const WEEKDAYS_FR = ['Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa', 'Di'];
const WEEKDAYS_LONG_FR = [
    'Lundi',
    'Mardi',
    'Mercredi',
    'Jeudi',
    'Vendredi',
    'Samedi',
    'Dimanche',
];
const MONTHS_FR = [
    'Janvier',
    'Février',
    'Mars',
    'Avril',
    'Mai',
    'Juin',
    'Juillet',
    'Août',
    'Septembre',
    'Octobre',
    'Novembre',
    'Décembre',
];
const DEFAULT_PLACEHOLDER = 'JJ/MM/AAAA';
// Amplitude du sélecteur d'année quand `min`/`max` ne la bornent pas.
const YEARS_BEFORE = 100;
const YEARS_AFTER = 5;
// Parse 'YYYY-MM-DD' en composants numériques. On évite `new Date('YYYY-MM-DD')`
// qui interprète la chaîne en UTC et peut décaler d'un jour selon le fuseau.
function parseISO(iso) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso ?? '');
    if (!match) {
        return null;
    }
    return { y: +match[1], m: +match[2] - 1, d: +match[3] };
}
function pad(n) {
    return String(n).padStart(2, '0');
}
function toISO(y, m, d) {
    return `${y}-${pad(m + 1)}-${pad(d)}`;
}
function daysInMonth(y, m) {
    return new Date(y, m + 1, 0).getDate();
}
function isWeekend(iso) {
    const p = parseISO(iso);
    if (!p) {
        return false;
    }
    const day = new Date(p.y, p.m, p.d).getDay();
    return day === 0 || day === 6;
}
const Calendar = createComponent((opts) => ({
    value: opts.value ?? '',
    min: opts.min ?? '',
    max: opts.max ?? '',
    defaultDate: opts.defaultDate || opts.min || '',
    placeholder: opts.placeholder || DEFAULT_PLACEHOLDER,
    excludeWeekends: opts.excludeWeekends ?? false,
    isOpen: false,
    viewYear: 0,
    viewMonth: 0,
    weekdaysFr: WEEKDAYS_FR,
    monthsFr: MONTHS_FR,
    init() {
        this.resetView();
    },
    // Vue initiale : la valeur déjà choisie, sinon `defaultDate`, sinon le mois
    // courant.
    resetView() {
        const now = new Date();
        const ref = parseISO(this.value) ??
            parseISO(this.defaultDate) ?? {
            y: now.getFullYear(),
            m: now.getMonth(),
            d: 1,
        };
        this.viewYear = ref.y;
        this.viewMonth = ref.m;
    },
    get days() {
        const y = Number(this.viewYear);
        const m = Number(this.viewMonth);
        const offset = (new Date(y, m, 1).getDay() + 6) % 7;
        const cells = [];
        for (let i = 0; i < offset; i++) {
            cells.push(null);
        }
        for (let d = 1; d <= daysInMonth(y, m); d++) {
            const iso = toISO(y, m, d);
            cells.push({
                day: d,
                iso,
                disabled: this.isDisabled(iso),
                selected: this.value === iso,
            });
        }
        return cells;
    },
    get displayValue() {
        const p = parseISO(this.value);
        if (!p) {
            return '';
        }
        const weekday = WEEKDAYS_LONG_FR[(new Date(p.y, p.m, p.d).getDay() + 6) % 7];
        return `${weekday} ${pad(p.d)}/${pad(p.m + 1)}/${p.y}`;
    },
    get years() {
        const current = new Date().getFullYear();
        const first = parseISO(this.min)?.y ?? current - YEARS_BEFORE;
        const last = parseISO(this.max)?.y ?? current + YEARS_AFTER;
        const years = [];
        for (let y = last; y >= first; y--) {
            years.push(y);
        }
        return years;
    },
    get canPrev() {
        if (!this.min) {
            return true;
        }
        return (toISO(Number(this.viewYear), Number(this.viewMonth), 1) > this.min);
    },
    get canNext() {
        const y = Number(this.viewYear);
        const m = Number(this.viewMonth);
        if (!this.max) {
            return true;
        }
        return toISO(y, m, daysInMonth(y, m)) < this.max;
    },
    isDisabled(iso) {
        if (this.min && iso < this.min) {
            return true;
        }
        if (this.max && iso > this.max) {
            return true;
        }
        return this.excludeWeekends && isWeekend(iso);
    },
    isMonthDisabled(y, m) {
        const first = toISO(y, m, 1);
        const last = toISO(y, m, daysInMonth(y, m));
        return ((!!this.min && last < this.min) || (!!this.max && first > this.max));
    },
    // Après un changement d'année, le mois affiché peut sortir de l'intervalle
    // autorisé : on le ramène sur le mois sélectionnable le plus proche.
    clampView() {
        const y = Number(this.viewYear);
        const m = Number(this.viewMonth);
        if (!this.isMonthDisabled(y, m)) {
            return;
        }
        const allowed = [];
        for (let i = 0; i < 12; i++) {
            if (!this.isMonthDisabled(y, i)) {
                allowed.push(i);
            }
        }
        if (!allowed.length) {
            return;
        }
        this.viewMonth =
            m < allowed[0] ? allowed[0] : allowed[allowed.length - 1];
    },
    select(iso) {
        if (this.isDisabled(iso)) {
            return;
        }
        this.value = iso;
        this.close();
    },
    prevMonth() {
        if (!this.canPrev) {
            return;
        }
        const m = Number(this.viewMonth);
        if (m === 0) {
            this.viewMonth = 11;
            this.viewYear = Number(this.viewYear) - 1;
        }
        else {
            this.viewMonth = m - 1;
        }
    },
    nextMonth() {
        if (!this.canNext) {
            return;
        }
        const m = Number(this.viewMonth);
        if (m === 11) {
            this.viewMonth = 0;
            this.viewYear = Number(this.viewYear) + 1;
        }
        else {
            this.viewMonth = m + 1;
        }
    },
    toggle() {
        this.isOpen = !this.isOpen;
    },
    open() {
        this.resetView();
        this.isOpen = true;
    },
    close() {
        this.isOpen = false;
    },
}));
const Datepicker = { Calendar };
export { Datepicker };
